/**
 * Utilities for handling antimeridian (International Date Line) crossings for polygons and linestrings.
 *
 * When users draw shapes in OpenLayers, coordinates can exceed the standard [-180, 180]
 * longitude range because OpenLayers uses a continuous coordinate system for map wrapping.
 * This module provides functions to detect, normalize, and handle such cases.
 *
 * Design constraints:
 * - Coordinates must be within [-540°, 540°] (one world width beyond ±180°)
 * - Longitude span must be < 360° (cannot wrap around the entire world)
 */

/** Maximum allowed longitude value (±540° = ±180° + one world width) */
export const MAX_LONGITUDE = 540;
/** Minimum allowed longitude value */
export const MIN_LONGITUDE = -540;

export interface LongitudeSpans {
  /** The raw longitude span from the original coordinates */
  rawSpan: number;
  /** The longitude span after normalizing to [-180, 180] */
  normalizedSpan: number;
  /** The span if treating the polygon as crossing the antimeridian */
  antimeridianSpan: number;
}

export interface AntimeridianCheckResult {
  /** Whether the geometry might cross the antimeridian */
  mightCross: boolean;
  /** Span information for both interpretations */
  spans: LongitudeSpans;
  /** The original coordinates (Polygon: number[][][], LineString: number[][]) */
  originalCoordinates: number[][] | number[][][];
  /** The geometry type */
  geometryType: 'Polygon' | 'LineString';
}

export interface CoordinateRangeValidation {
  /** Whether all coordinates are within the allowed ±540° range */
  isValid: boolean;
  /** Coordinates that are outside the allowed range */
  outOfRangeCoords: number[][];
  /** Minimum longitude found */
  minLon: number;
  /** Maximum longitude found */
  maxLon: number;
  /** The longitude span */
  span: number;
}

/**
 * Normalizes a longitude to the [-180, 180] range.
 *
 * @example
 * normalizeLongitude(209)   // returns -151
 * normalizeLongitude(-200)  // returns 160
 * normalizeLongitude(90)    // returns 90
 */
export function normalizeLongitude(lon: number): number {
  let normalized = lon;
  while (normalized > 180) {
    normalized -= 360;
  }
  while (normalized < -180) {
    normalized += 360;
  }
  return normalized;
}

/**
 * Checks if a polygon's raw coordinates suggest it might cross the antimeridian.
 *
 * Returns true if:
 * - Any longitude is outside the [-180, 180] range
 * - The longitudes span across the 180° line
 *
 * @param coordinates - GeoJSON polygon coordinates array (array of rings, each ring is array of [lon, lat])
 */
export function mightCrossAntimeridian(coordinates: number[][][]): boolean {
  const ring = coordinates[0];
  if (!ring || ring.length === 0) return false;

  const longitudes = ring.map(([lon]) => lon);

  const minLon = Math.min(...longitudes);
  const maxLon = Math.max(...longitudes);

  const hasOutOfRangeCoordinates = longitudes.some(
    (lon) => lon > 180 || lon < -180,
  );
  const spansAcross180 = minLon < 180 && maxLon > 180;

  return hasOutOfRangeCoordinates || spansAcross180;
}

/**
 * Calculates longitude span information for both interpretations.
 *
 * This helps understand the difference between:
 * - Treating it as a normal polygon (normalized)
 * - Treating it as crossing the antimeridian
 *
 * @param coordinates - GeoJSON polygon coordinates array
 */
export function calculateLongitudeSpans(
  coordinates: number[][][],
): LongitudeSpans {
  const ring = coordinates[0];
  if (!ring || ring.length === 0) {
    return { rawSpan: 0, normalizedSpan: 0, antimeridianSpan: 360 };
  }

  const longitudes = ring.map(([lon]) => lon);

  const minLon = Math.min(...longitudes);
  const maxLon = Math.max(...longitudes);
  const rawSpan = maxLon - minLon;

  const normalizedLongitudes = longitudes.map(normalizeLongitude);
  const normalizedMin = Math.min(...normalizedLongitudes);
  const normalizedMax = Math.max(...normalizedLongitudes);
  const normalizedSpan = normalizedMax - normalizedMin;

  const antimeridianSpan = 360 - normalizedSpan;

  return {
    rawSpan,
    normalizedSpan,
    antimeridianSpan,
  };
}

/**
 * Normalizes all coordinates in a polygon to [-180, 180] range.
 *
 * Use this when the user confirms they did NOT intend to cross the antimeridian.
 *
 * @param coordinates - GeoJSON polygon coordinates array
 * @returns New coordinates array with normalized longitudes
 */
export function normalizePolygonCoordinates(
  coordinates: number[][][],
): number[][][] {
  return coordinates.map((ring) =>
    ring.map(([lon, lat, ...rest]) => [normalizeLongitude(lon), lat, ...rest]),
  );
}

/**
 * Preserves antimeridian-crossing intent by shifting to [0, 360] range.
 *
 * Use this when the user confirms they DID intend to cross the antimeridian.
 * This keeps coordinates in a continuous space for backend compatibility.
 *
 * @param coordinates - GeoJSON polygon coordinates array
 * @returns New coordinates array with positive longitudes
 */
export function preserveAntimeridianCrossing(
  coordinates: number[][][],
): number[][][] {
  return coordinates.map((ring) =>
    ring.map(([lon, lat, ...rest]) => {
      const adjustedLon = lon < 0 ? lon + 360 : lon;
      return [adjustedLon, lat, ...rest];
    }),
  );
}

/**
 * Checks if a GeoJSON geometry might cross the antimeridian and returns
 * information needed to prompt the user for clarification.
 *
 * @param geometry - GeoJSON geometry object
 * @returns Check result with spans info, or null if not a polygon or doesn't need checking
 */
export function checkAntimeridianCrossing(geometry: {
  type: string;
  coordinates: number[] | number[][] | number[][][];
}): AntimeridianCheckResult | null {
  if (geometry.type !== 'Polygon') {
    return null;
  }

  const coordinates = geometry.coordinates as number[][][];

  if (!mightCrossAntimeridian(coordinates)) {
    return null;
  }

  return {
    mightCross: true,
    spans: calculateLongitudeSpans(coordinates),
    originalCoordinates: coordinates,
    geometryType: 'Polygon',
  };
}

/**
 * Processes a polygon geometry based on the user's antimeridian crossing decision.
 *
 * @param geometry - GeoJSON geometry object
 * @param crossesAntimeridian - Whether the polygon crosses the antimeridian
 * @returns New geometry object with processed coordinates
 */
export function processPolygonForAntimeridian<
  T extends { type: string; coordinates: number[] | number[][] | number[][][] },
>(geometry: T, crossesAntimeridian: boolean): T {
  if (geometry.type !== 'Polygon') {
    return geometry;
  }

  const coordinates = geometry.coordinates as number[][][];
  const processedCoordinates = crossesAntimeridian
    ? preserveAntimeridianCrossing(coordinates)
    : normalizePolygonCoordinates(coordinates);

  return {
    ...geometry,
    coordinates: processedCoordinates,
  };
}

/**
 * Normalizes a GeoJSON geometry, applying standard normalization to [-180, 180].
 * Use this for geometries that don't need antimeridian dialog confirmation.
 */
export function normalizeGeometry<
  T extends { type: string; coordinates: number[] | number[][] | number[][][] },
>(geometry: T): T {
  if (geometry.type !== 'Polygon') {
    return geometry;
  }

  return processPolygonForAntimeridian(geometry, false);
}

// =============================================================================
// COORDINATE RANGE VALIDATION (±540°)
// =============================================================================

/**
 * Extracts longitudes from coordinates, handling both Polygon and LineString structures.
 *
 * @param coordinates - Either LineString (number[][]) or Polygon (number[][][]) coordinates
 * @returns Array of [lon, lat] coordinate pairs
 */
function extractCoordinatePairs(
  coordinates: number[][] | number[][][],
): number[][] {
  if (coordinates.length === 0) return [];

  // Check if this is a Polygon (array of rings) or LineString (array of points)
  const firstElement = coordinates[0];
  if (Array.isArray(firstElement) && Array.isArray(firstElement[0])) {
    // Polygon: coordinates is number[][][] (array of rings)
    // Flatten all rings into a single array of coordinate pairs
    return (coordinates as number[][][]).flat();
  } else {
    // LineString: coordinates is number[][] (array of points)
    return coordinates as number[][];
  }
}

/**
 * Validates that all coordinates are within the allowed ±540° longitude range.
 *
 * This allows one "world width" beyond the standard ±180° range in either direction,
 * which is sufficient for any valid antimeridian-crossing geometry.
 *
 * @param coordinates - LineString (number[][]) or Polygon (number[][][]) coordinates
 * @returns Validation result with details about any out-of-range coordinates
 *
 * @example
 * // Valid polygon within range
 * validateCoordinateRange([[[170, 50], [190, 50], [190, 60], [170, 60], [170, 50]]])
 * // { isValid: true, outOfRangeCoords: [], minLon: 170, maxLon: 190, span: 20 }
 *
 * @example
 * // Invalid - coordinates exceed ±540°
 * validateCoordinateRange([[[550, 50], [560, 50], [560, 60], [550, 60], [550, 50]]])
 * // { isValid: false, outOfRangeCoords: [[550, 50], [560, 50], ...], minLon: 550, maxLon: 560, span: 10 }
 */
export function validateCoordinateRange(
  coordinates: number[][] | number[][][],
): CoordinateRangeValidation {
  const coordPairs = extractCoordinatePairs(coordinates);

  if (coordPairs.length === 0) {
    return {
      isValid: true,
      outOfRangeCoords: [],
      minLon: 0,
      maxLon: 0,
      span: 0,
    };
  }

  const longitudes = coordPairs.map(([lon]) => lon);
  const minLon = Math.min(...longitudes);
  const maxLon = Math.max(...longitudes);
  const span = maxLon - minLon;

  const outOfRangeCoords = coordPairs.filter(
    ([lon]) => lon > MAX_LONGITUDE || lon < MIN_LONGITUDE,
  );

  return {
    isValid: outOfRangeCoords.length === 0,
    outOfRangeCoords,
    minLon,
    maxLon,
    span,
  };
}

/**
 * Validates that the longitude span is less than 360° (one full world width).
 *
 * A geometry spanning 360° or more would overlap itself or cover the entire globe,
 * which is almost certainly an error.
 *
 * @param coordinates - LineString (number[][]) or Polygon (number[][][]) coordinates
 * @returns Whether the span is valid (< 360°)
 */
export function validateLongitudeSpan(coordinates: number[][] | number[][][]): {
  isValid: boolean;
  span: number;
} {
  const coordPairs = extractCoordinatePairs(coordinates);

  if (coordPairs.length === 0) {
    return { isValid: true, span: 0 };
  }

  const longitudes = coordPairs.map(([lon]) => lon);
  const minLon = Math.min(...longitudes);
  const maxLon = Math.max(...longitudes);
  const span = maxLon - minLon;

  return {
    isValid: span < 360,
    span,
  };
}

// =============================================================================
// LINESTRING ANTIMERIDIAN SUPPORT
// =============================================================================

/**
 * Checks if a LineString's raw coordinates suggest it might cross the antimeridian.
 *
 * Returns true if any longitude is outside the [-180, 180] range.
 *
 * @param coordinates - GeoJSON LineString coordinates array (array of [lon, lat])
 */
export function mightCrossAntimeridianLineString(
  coordinates: number[][],
): boolean {
  if (!coordinates || coordinates.length === 0) return false;

  const longitudes = coordinates.map(([lon]) => lon);

  const minLon = Math.min(...longitudes);
  const maxLon = Math.max(...longitudes);

  const hasOutOfRangeCoordinates = longitudes.some(
    (lon) => lon > 180 || lon < -180,
  );
  const spansAcross180 = minLon < 180 && maxLon > 180;

  return hasOutOfRangeCoordinates || spansAcross180;
}

/**
 * Calculates longitude span information for a LineString.
 *
 * @param coordinates - GeoJSON LineString coordinates array
 */
export function calculateLongitudeSpansLineString(
  coordinates: number[][],
): LongitudeSpans {
  if (!coordinates || coordinates.length === 0) {
    return { rawSpan: 0, normalizedSpan: 0, antimeridianSpan: 360 };
  }

  const longitudes = coordinates.map(([lon]) => lon);

  const minLon = Math.min(...longitudes);
  const maxLon = Math.max(...longitudes);
  const rawSpan = maxLon - minLon;

  const normalizedLongitudes = longitudes.map(normalizeLongitude);
  const normalizedMin = Math.min(...normalizedLongitudes);
  const normalizedMax = Math.max(...normalizedLongitudes);
  const normalizedSpan = normalizedMax - normalizedMin;

  const antimeridianSpan = 360 - normalizedSpan;

  return {
    rawSpan,
    normalizedSpan,
    antimeridianSpan,
  };
}

/**
 * Normalizes all coordinates in a LineString to [-180, 180] range.
 *
 * @param coordinates - GeoJSON LineString coordinates array
 * @returns New coordinates array with normalized longitudes
 */
export function normalizeLineStringCoordinates(
  coordinates: number[][],
): number[][] {
  return coordinates.map(([lon, lat, ...rest]) => [
    normalizeLongitude(lon),
    lat,
    ...rest,
  ]);
}

/**
 * Preserves antimeridian-crossing intent for LineString by shifting to [0, 360] range.
 *
 * @param coordinates - GeoJSON LineString coordinates array
 * @returns New coordinates array with positive longitudes
 */
export function preserveAntimeridianCrossingLineString(
  coordinates: number[][],
): number[][] {
  return coordinates.map(([lon, lat, ...rest]) => {
    const adjustedLon = lon < 0 ? lon + 360 : lon;
    return [adjustedLon, lat, ...rest];
  });
}

/**
 * Checks if a GeoJSON geometry (Polygon or LineString) might cross the antimeridian
 * and returns information needed to prompt the user for clarification.
 *
 * @param geometry - GeoJSON geometry object (Polygon or LineString)
 * @returns Check result with spans info, or null if doesn't need checking
 */
export function checkAntimeridianCrossingGeometry(geometry: {
  type: string;
  coordinates: number[] | number[][] | number[][][];
}): AntimeridianCheckResult | null {
  if (geometry.type === 'Polygon') {
    const coordinates = geometry.coordinates as number[][][];

    if (!mightCrossAntimeridian(coordinates)) {
      return null;
    }

    return {
      mightCross: true,
      spans: calculateLongitudeSpans(coordinates),
      originalCoordinates: coordinates,
      geometryType: 'Polygon',
    };
  }

  if (geometry.type === 'LineString') {
    const coordinates = geometry.coordinates as number[][];

    if (!mightCrossAntimeridianLineString(coordinates)) {
      return null;
    }

    return {
      mightCross: true,
      spans: calculateLongitudeSpansLineString(coordinates),
      originalCoordinates: coordinates,
      geometryType: 'LineString',
    };
  }

  return null;
}

/**
 * Processes a geometry based on the user's antimeridian crossing decision.
 * Works for both Polygon and LineString geometries.
 *
 * @param geometry - GeoJSON geometry object
 * @param crossesAntimeridian - Whether the geometry crosses the antimeridian
 * @returns New geometry object with processed coordinates
 */
export function processGeometryForAntimeridian<
  T extends { type: string; coordinates: number[] | number[][] | number[][][] },
>(geometry: T, crossesAntimeridian: boolean): T {
  if (geometry.type === 'Polygon') {
    const coordinates = geometry.coordinates as number[][][];
    const processedCoordinates = crossesAntimeridian
      ? preserveAntimeridianCrossing(coordinates)
      : normalizePolygonCoordinates(coordinates);

    return {
      ...geometry,
      coordinates: processedCoordinates,
    };
  }

  if (geometry.type === 'LineString') {
    const coordinates = geometry.coordinates as number[][];
    const processedCoordinates = crossesAntimeridian
      ? preserveAntimeridianCrossingLineString(coordinates)
      : normalizeLineStringCoordinates(coordinates);

    return {
      ...geometry,
      coordinates: processedCoordinates,
    };
  }

  return geometry;
}
